import React from "react";
import FileUploadField from "./FileUploadField";
import { THEME_PRESETS } from "../utils/businessThemes";

/*
  Business Website manager — "Admin" tab (CR B1).
  Company factsheet incl. PAN/VAT rename (CR B9), theme colour (CR B8),
  Portfolio/Catalog/Brochure upload + external link (CR B5/B7),
  social links, and operating hours.
*/
export default function BizAdminTab({ form, set, hours, setHours, updList, personaluid }) {
    return (
        <>
            <div className="card shadow-sm mb-3">
                <div className="card-header bg-light fw-semibold">Company Factsheet</div>
                <div className="card-body"><div className="row g-2">
                    <div className="col-md-6"><label className="form-label small">Nature of Business</label><input className="form-control form-control-sm" placeholder="Retailer / Wholesaler / Manufacturer" value={form.nature_of_business} onChange={(e) => set("nature_of_business", e.target.value)} /></div>
                    <div className="col-md-6"><label className="form-label small">Year of Establishment</label><input className="form-control form-control-sm" value={form.year_established} onChange={(e) => set("year_established", e.target.value)} /></div>
                    <div className="col-md-6"><label className="form-label small">Legal Status of Firm</label><input className="form-control form-control-sm" placeholder="Proprietorship / Pvt Ltd" value={form.legal_status} onChange={(e) => set("legal_status", e.target.value)} /></div>
                    <div className="col-md-6"><label className="form-label small">No. of Employees</label><input className="form-control form-control-sm" value={form.employees} onChange={(e) => set("employees", e.target.value)} /></div>
                    <div className="col-md-6"><label className="form-label small">Annual Turnover</label><input className="form-control form-control-sm" value={form.annual_turnover} onChange={(e) => set("annual_turnover", e.target.value)} /></div>
                    {/* CR B9: label renamed from "GST No." — underlying field/DB column (gst_no) is unchanged */}
                    <div className="col-md-6"><label className="form-label small">PAN / VAT Number</label><input className="form-control form-control-sm" value={form.gst_no} onChange={(e) => set("gst_no", e.target.value)} /></div>
                    {/* B11: Trustseal — manual badge shown below the company name on the live site,
                        separate from the package-based "Verified" flag. */}
                    <div className="col-md-6 d-flex align-items-center">
                        <div className="form-check mt-4">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="bizTrustseal"
                                checked={!!form.trustseal}
                                onChange={(e) => set("trustseal", e.target.checked)}
                            />
                            <label className="form-check-label small" htmlFor="bizTrustseal">
                                Show "Trustseal" badge on my website
                            </label>
                        </div>
                    </div>
                    {/* B11: Rating — manual starting rating; shown on the live site only until
                        real customer reviews exist, then the genuine review average takes over. */}
                    <div className="col-md-6">
                        <label className="form-label small">Rating (out of 5)</label>
                        <input
                            type="number"
                            min="0"
                            max="5"
                            step="0.1"
                            className="form-control form-control-sm"
                            placeholder="e.g. 4.5"
                            value={form.manual_rating}
                            onChange={(e) => set("manual_rating", e.target.value)}
                        />
                        <div className="form-text">Used only until your listing has real customer reviews.</div>
                    </div>
                </div></div>
            </div>

            {/* CR B8: Theme colour selection */}
            <div className="card shadow-sm mb-3">
                <div className="card-header bg-light fw-semibold">Website Theme Colour</div>
                <div className="card-body">
                    <div className="d-flex flex-wrap gap-2">
                        {THEME_PRESETS.map((t) => (
                            <button
                                type="button"
                                key={t.key}
                                onClick={() => set("theme_color", t.key)}
                                title={t.label}
                                className="dn-theme-swatch"
                                style={{
                                    background: `linear-gradient(135deg, ${t.brand}, ${t.brand2})`,
                                    outline: (form.theme_color || "classic-navy") === t.key ? `2px solid ${t.accent}` : "2px solid transparent",
                                }}
                            >
                                {(form.theme_color || "classic-navy") === t.key && <i className="bi bi-check-lg" />}
                            </button>
                        ))}
                    </div>
                    <div className="form-text mt-2">
                        Selected: <b>{THEME_PRESETS.find((t) => t.key === form.theme_color)?.label || "Classic Navy"}</b> — this changes the colours on your live business website.
                    </div>
                    <style>{`
                        .dn-theme-swatch{width:38px;height:38px;border-radius:10px;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;color:#fff;font-size:16px;transition:transform .15s ease;}
                        .dn-theme-swatch:hover{transform:scale(1.08);}
                    `}</style>
                </div>
            </div>

            {/* CR B5 / B7: Portfolio / Catalog / Brochure */}
            <div className="card shadow-sm mb-3">
                <div className="card-header bg-light fw-semibold">Portfolio / Catalog / Brochure</div>
                <div className="card-body">
                    <div className="mb-3">
                        <label className="form-label small">Upload File</label>
                        <FileUploadField value={form.brochure_file} onChange={(v) => set("brochure_file", v)} personaluid={personaluid} />
                    </div>
                    <div>
                        <label className="form-label small">Or an external link (Google Drive, etc.)</label>
                        <input
                            className="form-control form-control-sm"
                            placeholder="https://drive.google.com/..."
                            value={form.brochure_link}
                            onChange={(e) => set("brochure_link", e.target.value)}
                        />
                        <div className="form-text">Visitors will see a "Download Portfolio/Catalog" button on your website using whichever of these is filled in (the uploaded file takes priority).</div>
                    </div>
                </div>
            </div>

            {/* HOURS */}
            <div className="card shadow-sm mb-3">
                <div className="card-header bg-light fw-semibold d-flex justify-content-between align-items-center">
                    <span>Operating Hours</span>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setHours([...hours, { day: "", time: "" }])}>+ Add</button>
                </div>
                <div className="card-body">
                    {hours.map((h, i) => (
                        <div className="row g-2 mb-2" key={i}>
                            <div className="col-md-5"><input placeholder="Day (e.g. Sun - Fri)" className="form-control form-control-sm" value={h.day} onChange={(e) => updList(hours, setHours, i, "day", e.target.value)} /></div>
                            <div className="col-md-6"><input placeholder="Time (e.g. 9AM - 6PM or Closed)" className="form-control form-control-sm" value={h.time} onChange={(e) => updList(hours, setHours, i, "time", e.target.value)} /></div>
                            <div className="col-md-1"><button type="button" className="btn btn-sm btn-outline-danger w-100" onClick={() => setHours(hours.filter((_, x) => x !== i))}>×</button></div>
                        </div>
                    ))}
                </div>
            </div>

            {/* SOCIAL */}
            <div className="card shadow-sm mb-3">
                <div className="card-header bg-light fw-semibold">Social Links</div>
                <div className="card-body"><div className="row g-2">
                    <div className="col-md-4"><label className="form-label small">Facebook</label><input className="form-control form-control-sm" value={form.linkfacebook} onChange={(e) => set("linkfacebook", e.target.value)} /></div>
                    <div className="col-md-4"><label className="form-label small">Instagram</label><input className="form-control form-control-sm" value={form.linkinstagram} onChange={(e) => set("linkinstagram", e.target.value)} /></div>
                    <div className="col-md-4"><label className="form-label small">LinkedIn</label><input className="form-control form-control-sm" value={form.linklinkedin} onChange={(e) => set("linklinkedin", e.target.value)} /></div>
                </div></div>
            </div>
        </>
    );
}